import { useEffect, useState } from "react";
import { useRewards, type RewardsData, type PointsHistoryItem } from "@/hooks/useRewards";

interface RewardsCardProps {
  customerId: string;
}

export default function RewardsCard({ customerId }: RewardsCardProps) {
  const { getRewards, getPointsHistory } = useRewards();
  const [rewards, setRewards] = useState<RewardsData | null>(null);
  const [history, setHistory] = useState<PointsHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    Promise.all([
      getRewards(customerId),
      getPointsHistory(customerId),
    ]).then(([r, h]) => {
      setRewards(r);
      setHistory(h);
      setLoading(false);
    });
  }, [customerId, getRewards, getPointsHistory]);

  if (loading) {
    return (
      <div className="bg-[#111] border border-white/8 rounded-3xl p-5 animate-pulse">
        <div className="h-4 bg-white/5 rounded w-1/3 mb-4"></div>
        <div className="h-16 bg-white/5 rounded-2xl"></div>
      </div>
    );
  }

  if (!rewards) return null;

  const tierGradients: Record<string, string> = {
    bronze:   "from-amber-700/20 to-amber-700/5",
    silver:   "from-gray-400/20 to-gray-400/5",
    gold:     "from-amber-400/20 to-amber-400/5",
    platinum: "from-cyan-300/20 to-cyan-300/5",
  };

  const tierBarColors: Record<string, string> = {
    bronze:   "bg-amber-700",
    silver:   "bg-gray-300",
    gold:     "bg-amber-400",
    platinum: "bg-cyan-300",
  };

  const tierTopBars: Record<string, string> = {
    bronze:   "from-amber-700/0 via-amber-700/60 to-amber-700/0",
    silver:   "from-gray-300/0 via-gray-300/60 to-gray-300/0",
    gold:     "from-amber-400/0 via-amber-400/60 to-amber-400/0",
    platinum: "from-cyan-300/0 via-cyan-300/60 to-cyan-300/0",
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main Rewards Card */}
      <div className={`bg-gradient-to-br ${tierGradients[rewards.tier]} border ${rewards.tierBorder} rounded-3xl p-5 relative overflow-hidden`}>
        {/* Top accent bar */}
        <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r ${tierTopBars[rewards.tier]}`}></div>

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 flex items-center justify-center ${rewards.tierBg} border ${rewards.tierBorder} rounded-xl`}>
              <i className={`${rewards.tierIcon} ${rewards.tierColor} text-lg`}></i>
            </div>
            <div>
              <p className="text-white text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>نقاط المكافآت</p>
              <p className={`text-xs font-bold ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                عضو {rewards.tierLabel}
              </p>
            </div>
          </div>
          <div className="text-left">
            <p className={`text-3xl font-black ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.points.toLocaleString()}
            </p>
            <p className="text-gray-500 text-xs text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة متاحة</p>
          </div>
        </div>

        {/* Progress to next tier */}
        {rewards.nextTierPoints !== null && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {rewards.nextTierPoints} نقطة للوصول لـ
                <span className={`font-black mr-1 ${rewards.tierColor}`}>{rewards.nextTierLabel}</span>
              </span>
              <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {rewards.progressPercent}%
              </span>
            </div>
            <div className="h-2 bg-white/8 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${tierBarColors[rewards.tier]}`}
                style={{ width: `${rewards.progressPercent}%` }}
              ></div>
            </div>
          </div>
        )}

        {rewards.tier === "platinum" && (
          <div className={`flex items-center gap-2 ${rewards.tierBg} border ${rewards.tierBorder} rounded-xl px-3 py-2 mb-4`}>
            <i className={`ri-vip-diamond-line ${rewards.tierColor} text-sm`}></i>
            <div>
              <span className={`text-xs font-bold ${rewards.tierColor} block`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                أنت في أعلى مستوى — عضو بلاتيني مميز!
              </span>
              <span className="text-cyan-300/70 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تستمتع بخصم 5% تلقائي على كل طلب
              </span>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-black/20 rounded-xl px-3 py-2.5 text-center">
            <p className={`text-base font-black ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.points}
            </p>
            <p className="text-gray-500 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة حالية</p>
          </div>
          <div className="bg-black/20 rounded-xl px-3 py-2.5 text-center">
            <p className="text-base font-black text-white" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.totalPointsEarned}
            </p>
            <p className="text-gray-500 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>إجمالي مكتسب</p>
          </div>
          <div className="bg-black/20 rounded-xl px-3 py-2.5 text-center">
            <p className="text-base font-black text-green-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {history.length}
            </p>
            <p className="text-gray-500 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>طلب مكافأ</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-[#111] border border-white/8 rounded-3xl p-5">
        <h4 className="text-white text-sm font-black mb-4 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-information-line text-amber-400"></i>
          كيف يعمل نظام النقاط؟
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-lg flex-shrink-0">
              <i className="ri-shopping-cart-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>اكسب نقاط</p>
              <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                نقطة لكل 10 ر.س تنفقها
              </p>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-green-400/10 rounded-lg flex-shrink-0">
              <i className="ri-gift-line text-green-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>استبدل نقاطك</p>
              <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                1000 نقطة = طلب بـ 100 ر.س مجاناً
              </p>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-700/10 rounded-lg flex-shrink-0">
              <i className="ri-medal-line text-amber-700 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>برونزي</p>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>0 — 99 نقطة</p>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-gray-400/10 rounded-lg flex-shrink-0">
              <i className="ri-medal-2-line text-gray-300 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>فضي</p>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>100 — 299 نقطة</p>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-lg flex-shrink-0">
              <i className="ri-vip-crown-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>ذهبي</p>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>300 — 599 نقطة</p>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 flex items-start gap-3 col-span-2">
            <div className="w-8 h-8 flex items-center justify-center bg-cyan-300/10 rounded-lg flex-shrink-0">
              <i className="ri-vip-diamond-line text-cyan-300 text-sm"></i>
            </div>
            <div>
              <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>بلاتيني</p>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>600+ نقطة — أعلى مستوى مع مزايا حصرية</p>
            </div>
          </div>
        </div>
      </div>

      {/* Points History */}
      {history.length > 0 && (
        <div className="bg-[#111] border border-white/8 rounded-3xl p-5">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between cursor-pointer"
          >
            <h4 className="text-white text-sm font-black flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-history-line text-amber-400"></i>
              سجل النقاط
              <span className="bg-amber-400/10 border border-amber-400/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {history.length}
              </span>
            </h4>
            <i className={`${showHistory ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-500 text-lg`}></i>
          </button>

          {showHistory && (
            <div className="mt-4 flex flex-col gap-2">
              {history.map((item, idx) => {
                const date = new Date(item.date).toLocaleDateString("ar-SA", {
                  year: "numeric", month: "short", day: "numeric",
                });
                return (
                  <div key={idx} className="flex items-center gap-3 bg-[#1a1a1a] rounded-xl px-4 py-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-400/10 rounded-xl flex-shrink-0">
                      <i className="ri-add-circle-line text-green-400 text-base"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        طلب #{item.orderRef}
                      </p>
                      <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        {date} · {item.orderTotal.toLocaleString()} ر.س
                      </p>
                    </div>
                    <div className="text-left flex-shrink-0">
                      <p className="text-green-400 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        +{item.pointsEarned}
                      </p>
                      <p className="text-gray-600 text-[10px] text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {history.length === 0 && (
        <div className="bg-[#111] border border-white/8 rounded-3xl px-5 py-8 flex flex-col items-center gap-3 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-400/10 rounded-2xl">
            <i className="ri-gift-line text-amber-400 text-2xl"></i>
          </div>
          <div>
            <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>لم تكسب نقاطاً بعد</p>
            <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              اطلب الآن واكسب نقاط مكافآت على كل طلب مؤكد
            </p>
          </div>
          <a
            href="/"
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-5 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-store-2-line"></i>
            تسوق الآن
          </a>
        </div>
      )}
    </div>
  );
}
